import { useEffect, useEffectEvent, useRef, type RefObject } from 'react';
import { Predicate } from 'effect';

/**
 * Tell the read-along follow engine, BY CONSTRUCTION, about every geometry
 * change so a paused viewport never keeps pointing at stale layout. A
 * ResizeObserver on the scroll container catches window/viewport resizes and
 * browser zoom; one on the content element catches app font-size / line-height
 * / paragraph-spacing / PDF-scale changes — all of them resize the measured
 * content box. That covers the whole class of "geometry moved while playback
 * was paused" without the reader enumerating individual preferences.
 *
 * Coalesced to one call per animation frame so a continuous zoom/resize drag
 * doesn't spam the controller (layoutInvalidated is idempotent — it just marks
 * the engine dirty and forces a re-resolve).
 *
 * Robust to late mount / remount: the observed set is re-synced every render
 * from the refs, because a reader may swap its loading skeleton for the real
 * scroll container (new DOM elements) after data loads.
 *
 * Caveat (virtualized readers): the content element is the total-size spacer,
 * whose height also shifts as TanStack Virtual measures freshly-mounted rows
 * mid-scroll — so this can fire during a follow scroll, not only on genuine
 * geometry changes. That is tolerable because the engine is idle between the
 * (infrequent) drift-correction scrolls, and a layoutInvalidated while idle
 * merely re-resolves to the same target; the only visible cost is a rare
 * re-issued scroll if a measurement lands mid-glide. Scrolling itself never
 * resizes the container, so the container observation stays clean.
 */
export function useLayoutInvalidation(
  onInvalidate: () => void,
  containerRef: RefObject<HTMLElement | null>,
  contentRef: RefObject<HTMLElement | null>,
) {
  const invalidate = useEffectEvent(() => onInvalidate());
  const frameRef = useRef(0);
  const observerRef = useRef<ResizeObserver | null>(null);
  const observedRef = useRef(new Set<Element>());

  // Re-sync observed elements after every commit: refs don't notify on change,
  // and the observed container/content can remount (loading → loaded).
  useEffect(() => {
    if (!Predicate.isFunction(globalThis.ResizeObserver)) return;
    let observer = observerRef.current;
    if (!observer) {
      // Skip the delivery ResizeObserver fires synchronously on the first
      // observe() so mount doesn't issue a redundant invalidation.
      let primed = false;
      observer = new ResizeObserver(() => {
        if (!primed) {
          primed = true;
          return;
        }
        if (frameRef.current) return;
        frameRef.current = requestAnimationFrame(() => {
          frameRef.current = 0;
          invalidate();
        });
      });
      observerRef.current = observer;
    }

    const observed = observedRef.current;
    const want = new Set<Element>();
    if (containerRef.current) want.add(containerRef.current);
    if (contentRef.current) want.add(contentRef.current);
    for (const el of observed)
      if (!want.has(el)) {
        observer.unobserve(el);
        observed.delete(el);
      }
    for (const el of want)
      if (!observed.has(el)) {
        observer.observe(el);
        observed.add(el);
      }
  });

  useEffect(
    () => () => {
      observerRef.current?.disconnect();
      observerRef.current = null;
      observedRef.current.clear();
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
    },
    [],
  );
}
